import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { CustomColumnType } from '../../../types/lead';
import { CUSTOM_COLUMN_TYPES } from './types';
import { MANAGE_COLUMNS_DIALOG_MAX_WIDTH, getManageColumnsDialogPaperProps } from '../../../utils/leads/leadsUtils';
import { ChoiceOptionsSection } from './ChoiceOptionsSection';
import { UpdateColumnConfirmDialog } from './UpdateColumnConfirmDialog';
import { DiscardChangesConfirmDialog } from './DiscardChangesConfirmDialog';

interface EditChoiceColumnDialogProps {
  open: boolean;
  onClose: () => void;
  columnType?: CustomColumnType;
  editingName: string;
  onEditingNameChange: (val: string) => void;
  renameError: string | null;
  options: string[];
  initialOptions?: string[];
  initialCount: number;
  addInput: string;
  onAddInputChange: (val: string) => void;
  onAddOption: () => void;
  addError: string | null;
  onClearAddError?: () => void;
  onOptionRename: (index: number, newLabel: string) => void;
  onRemoveOption: (index: number) => void;
  optionErrors: Record<number, string>;
  onSave: () => void;
  isModified?: boolean;
  hasOptionRenames?: boolean;
}

export const EditChoiceColumnDialog: React.FC<EditChoiceColumnDialogProps> = React.memo(({
  open,
  onClose,
  columnType,
  editingName,
  onEditingNameChange,
  renameError,
  options,
  initialOptions = [],
  initialCount,
  addInput,
  onAddInputChange,
  onAddOption,
  addError,
  onClearAddError,
  onOptionRename,
  onRemoveOption,
  optionErrors,
  onSave,
  isModified = false,
  hasOptionRenames = false,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState<boolean>(false);
  const [colNameWarning, setColNameWarning] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const typeConfig = CUSTOM_COLUMN_TYPES.find((t) => t.type === columnType);
  const TypeIcon = typeConfig?.icon;

  const hasErrors = Boolean(renameError) || Boolean(addError) || Object.keys(optionErrors).length > 0;
  const isSaveDisabled = !isModified || !editingName?.trim() || hasErrors;

  const handleRequestClose = () => {
    if (isModified) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false);
    onClose();
  };

  const handleInitiateSave = () => {
    if (hasOptionRenames) {
      setShowConfirmDialog(true);
    } else {
      onSave();
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmDialog(false);
    onSave();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleRequestClose}
        maxWidth={MANAGE_COLUMNS_DIALOG_MAX_WIDTH}
        fullWidth
        fullScreen={isMobile}
        PaperProps={getManageColumnsDialogPaperProps(isMobile)}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'transparent' },
          },
        }}
      >
        {/* Header Bar matching ManageLeadColumnsDialog */}
        <DialogTitle sx={{ flexShrink: 0, pt: 2.5, px: 3, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography variant="h6" fontWeight="700" sx={{ letterSpacing: '-0.025em', color: 'text.primary' }}>
                Edit Column
              </Typography>
              {typeConfig && (
                <Chip
                  icon={TypeIcon ? <TypeIcon fontSize="small" /> : undefined}
                  label={typeConfig.label}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: '6px',
                    bgcolor: 'rgba(37, 99, 235, 0.04)',
                  }}
                />
              )}
            </Box>
            <IconButton
              onClick={handleRequestClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Content Body without double scrollbars */}
        <DialogContent sx={{ p: isMobile ? 2 : 3, py: 1.5, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
          {/* Column Name Field */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Column Name
            </Typography>

            <TextField
              size="small"
              fullWidth
              variant="outlined"
              value={editingName || ''}
              onChange={(e) => {
                const val = e.target.value;
                onEditingNameChange(val);
                setColNameWarning(val.length >= 25);
              }}
              onFocus={(e) => setColNameWarning(e.target.value.length >= 25)}
              onBlur={() => setColNameWarning(false)}
              autoFocus
              placeholder="Enter Column Name"
              error={Boolean(renameError)}
              inputProps={{ maxLength: 25 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: 38,
                  fontSize: '0.875rem',
                  bgcolor: '#FFFFFF',
                  borderColor: colNameWarning ? 'warning.main' : undefined,
                },
              }}
            />
            {colNameWarning && !renameError && (
              <Typography variant="caption" sx={{ color: 'warning.main', fontSize: '0.75rem', fontWeight: 500 }}>
                Column name cannot exceed 25 characters
              </Typography>
            )}
            {renameError && (
              <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.75rem', fontWeight: 500 }}>
                {renameError}
              </Typography>
            )}
          </Box>

          {/* Choice Options Section */}
          <ChoiceOptionsSection
            options={options}
            initialOptions={initialOptions}
            initialCount={initialCount}
            addInput={addInput}
            onAddInputChange={onAddInputChange}
            onAddOption={onAddOption}
            addError={addError}
            onClearAddError={onClearAddError}
            onOptionRename={onOptionRename}
            onRemoveOption={onRemoveOption}
            optionErrors={optionErrors}
          />
        </DialogContent>

        {/* Sticky Actions Footer */}
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            flexShrink: 0,
            position: 'sticky',
            bottom: 0,
            bgcolor: '#FFFFFF',
            zIndex: 10,
          }}
        >
          <Button onClick={handleRequestClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleInitiateSave}
            disabled={isSaveDisabled}
            startIcon={<SaveIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              boxShadow: 'none',
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard Confirmation Dialog */}
      <DiscardChangesConfirmDialog
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
        onConfirmDiscard={handleConfirmDiscard}
      />

      {/* Confirmation Dialog before updating existing lead data */}
      <UpdateColumnConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  );
});

EditChoiceColumnDialog.displayName = 'EditChoiceColumnDialog';
